import axios from 'axios';
import eventsService from '../Events';

const sleep = (ms) => {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
};

/** Make api request
 * @param {string} method Http request type.
 * @param {string} url The url to call.
 * @param {object} params The request parameters, url parameters, or null.
 * @param {object} data Body data to send, post data, or null.
 * @param {integer} timeout Both timeout for request and the wait time before issuing a new.
 * @param {AbortSignal} signal An AbortController signal or null.
 * @param {boolean} useToasts Allow poll() to send toasts about http status/result. Defaults to true.
 * @returns {Promise} An axios promise
 */
const request = async ({ method, url, params, data, timeout = 10000, signal = null, useToasts = true }) => {
	const instance = axios.create({ timeout: timeout, baseURL: new URL(window.location.href).origin + '/api' });
	instance.interceptors.response.use(
		(response) => {
			return response;
		},
		(error) => {
			if (error.response && error.response.status === 401) {
				if (useToasts) {
					eventsService.triggerEvent('alert', {
						type: 'error',
						message: error.response.statusText,
					});
				}
				// Could do a token refresh here transparently and then retry error.response.request;
			}
			return Promise.reject(error);
		}
	);

	// Returns a Promise
	return instance({
		method: method,
		url: url,
		params: params ? params : null,
		data: data ? data : null,
		responseType: 'json', // default
		responseEncoding: 'utf8', // default
		validateStatus: function (status) {
			return status >= 200 && status < 300; // default.
		},
		signal: signal,
	}).catch(function (error) {
		if (error.response) {
			// response failed the validateStatus() check'
			if (useToasts) {
				eventsService.triggerEvent('alert', {
					type: 'error',
					message: error.response.statusText,
				});
			}
		} else if (error.request) {
			// No response within timeout
			if (useToasts) {
				eventsService.triggerEvent('alert', {
					type: 'error',
					message: error.message,
				});
			}
		} else {
			// instance creation failed
			if (useToasts) {
				eventsService.triggerEvent('alert', {
					type: 'error',
					message: 'Axios configuration error',
				});
			}
		}
		throw error;
	});
};

/** Poll api waiting for a particular value to change in response.
 * @param {string} method Http request type.
 * @param {string} url The url to call.
 * @param {object} params The request parameters, url parameters, or null.
 * @param {object} data Body data to send, post data, or null.
 * @param {integer} timeout Both timeout for request and the wait time before issuing a new.
 * @param {AbortSignal} signal An AbortController signal or null.
 * @param {integer} retries The number of times to retry request.
 * @param {function} RetryValidator A function that should return true if a retry should be made. The response payload is the only argument.
 * @param {function} onRetry A function called on every retry. The attempt number is the only argument.
 * @param {boolean} useToasts Allow poll() to send toasts about http status/result. Defaults to true.
 * @returns {Promise} An axios promise
 */
const poll = async ({
	method,
	url,
	params,
	data,
	timeout = 10000,
	signal = null,
	retries = 0,
	retryValidator = null,
	onRetry = null,
	useToasts = true,
}) => {
	let attempt = 0;
	let cnt = 1;
	while (attempt <= retries) {
		try {
			onRetry(cnt);
			const res = await request({
				method: method,
				url: url,
				params: params,
				data: data,
				timeout: timeout,
				signal: signal,
				useToasts: useToasts,
			});
			if (typeof retryValidator === 'function' && retryValidator(res.data)) {
				throw new Error('Response does not contain expected value. Retrying.');
			}
			return res;
		} catch (err) {
			if (err.response && err.response.status === 500) {
				throw new Error('Server responded with an error. Aborting.');
			}
			attempt++;
			cnt = parseInt(attempt) + 1;
			if (attempt >= retries) {
				throw new Error('Polling exceeded retry restraint. Aborting.');
			}
			await sleep(parseInt(timeout) + 500);
		}
	}
};

export { request, poll };
